import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Target, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Goal {
  id: string;
  name: string;
  current: number;
  target: number;
  unit?: string;
  completed?: boolean;
}

interface GoalTrackerWidgetProps {
  config: {
    title?: string;
    showCompleted?: boolean;
    maxDisplay?: number;
  };
  data?: {
    goals?: Goal[];
  };
}

export function GoalTrackerWidget({ config, data }: GoalTrackerWidgetProps) {
  const title = config.title || 'Goals';
  const goals = data?.goals || [];
  const maxDisplay = config.maxDisplay || 5;
  const showCompleted = config.showCompleted !== false;

  const displayGoals = showCompleted 
    ? goals.slice(0, maxDisplay)
    : goals.filter(g => !g.completed).slice(0, maxDisplay);

  const completedCount = goals.filter(g => g.completed || g.current >= g.target).length;

  if (goals.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">No goals set</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {completedCount}/{goals.length} complete
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayGoals.map((goal) => {
          const progress = Math.min((goal.current / goal.target) * 100, 100);
          const isComplete = goal.completed || goal.current >= goal.target;

          return (
            <div key={goal.id} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {isComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className={cn(isComplete && "text-muted-foreground line-through")}>
                    {goal.name}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {goal.current}/{goal.target} {goal.unit || ''}
                </span>
              </div>
              <Progress 
                value={progress} 
                className={cn("h-1.5", isComplete && "opacity-50")} 
              />
            </div>
          );
        })}

        {goals.length > maxDisplay && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            +{goals.length - maxDisplay} more goals
          </p>
        )}
      </CardContent>
    </Card>
  );
}
